/**
 * Generated by orval v6.23.0 🍺
 * Do not edit manually.
 * APIs for RCI FE
 * This document describes the APIs provided by NeoAP BE for RCI FE, designed to be integrated into NeoAP FE using `iframe`.\
These APIs utilize cookie-based authentication. All API requests must include the cookie `_ap_session`.

 * OpenAPI spec version: 1
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import type { AdminInvoiceReportApprovalRouteResponse } from '../model/adminInvoiceReportApprovalRouteResponse';
import type { AdminInvoiceReportBusinessDocumentResponse } from '../model/adminInvoiceReportBusinessDocumentResponse';
import type { AdminInvoiceReportPaymentInfoResponse } from '../model/adminInvoiceReportPaymentInfoResponse';
import type { BadRequestResponse } from '../model/badRequestResponse';
import type { BulkDestroyAdminInvoiceReports200 } from '../model/bulkDestroyAdminInvoiceReports200';
import type { BulkDestroyInvoiceReportsRequest } from '../model/bulkDestroyInvoiceReportsRequest';
import type { CancelInvoiceReportResponse } from '../model/cancelInvoiceReportResponse';
import type { CommonError } from '../model/commonError';
import type { DeleteInvoiceReportViewersRequest } from '../model/deleteInvoiceReportViewersRequest';
import type { ForbiddenResponse } from '../model/forbiddenResponse';
import type { GetAdminInvoiceReportSummaryResponse } from '../model/getAdminInvoiceReportSummaryResponse';
import type { GetInvoiceReportViewersResponse } from '../model/getInvoiceReportViewersResponse';
import type { InvoiceReportJournalResponse } from '../model/invoiceReportJournalResponse';
import type { InvoiceTransactionsResponse } from '../model/invoiceTransactionsResponse';
import type { NotFoundResponse } from '../model/notFoundResponse';
import type { PutInvoiceReportViewersRequest } from '../model/putInvoiceReportViewersRequest';
import type { SearchAdminInvoiceReports200One } from '../model/searchAdminInvoiceReports200One';
import type { SearchAdminInvoiceReportsParams } from '../model/searchAdminInvoiceReportsParams';
import type { SearchAdminInvoiceReportsRequest } from '../model/searchAdminInvoiceReportsRequest';
import type { UnauthorizedResponse } from '../model/unauthorizedResponse';
import type { UnprocessableEntityResponse } from '../model/unprocessableEntityResponse';
import { useCustomClient } from '../../mutator/custom-instance';
import type { ErrorType, BodyType } from '../../mutator/custom-instance';
import { customFomDataFn } from '../../api/formData/custom-function';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P
) => any
  ? P
  : never;

/**
 * Admin/observer only. `Accept: application/json` — paginated list; `Accept: text/csv` — CSV export (same `query` / `order_by`; `pagination` ignored).

CSV: ≤300 rows → `text/csv`; >300 → `AsyncCsvExportResponse` JSON (`redirect_url`, typically `/tempfiles`). Mimic CSV export → `403` (`FORBIDDEN_MIMIC_MODE`).

 * @summary Search admin invoice reports or export CSV
 */
export const useSearchAdminInvoiceReportsHook = () => {
  const searchAdminInvoiceReports = useCustomClient<
    SearchAdminInvoiceReports200One | Blob
  >();

  return (
    searchAdminInvoiceReportsRequest: BodyType<SearchAdminInvoiceReportsRequest>,
    params?: SearchAdminInvoiceReportsParams,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return searchAdminInvoiceReports(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/search`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: searchAdminInvoiceReportsRequest,
        params,
      },
      options
    );
  };
};

export const getSearchAdminInvoiceReportsQueryKey = (
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams
) => {
  return [
    `/api/js/invoice_reports/admin_invoice_reports/search`,
    ...(params ? [params] : []),
    searchAdminInvoiceReportsRequest,
  ] as const;
};

export const useSearchAdminInvoiceReportsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getSearchAdminInvoiceReportsQueryKey(
      searchAdminInvoiceReportsRequest,
      params
    );

  const searchAdminInvoiceReports = useSearchAdminInvoiceReportsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>
  > = () =>
    searchAdminInvoiceReports(
      searchAdminInvoiceReportsRequest,
      params,
      requestOptions
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type SearchAdminInvoiceReportsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>
>;
export type SearchAdminInvoiceReportsQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Search admin invoice reports or export CSV
 */
export const useSearchAdminInvoiceReports = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useSearchAdminInvoiceReportsQueryOptions(
    searchAdminInvoiceReportsRequest,
    params,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useSearchAdminInvoiceReportsSuspenseQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getSearchAdminInvoiceReportsQueryKey(
      searchAdminInvoiceReportsRequest,
      params
    );

  const searchAdminInvoiceReports = useSearchAdminInvoiceReportsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>
  > = () =>
    searchAdminInvoiceReports(
      searchAdminInvoiceReportsRequest,
      params,
      requestOptions
    );

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type SearchAdminInvoiceReportsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>
>;
export type SearchAdminInvoiceReportsSuspenseQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Search admin invoice reports or export CSV
 */
export const useSearchAdminInvoiceReportsSuspense = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useSearchAdminInvoiceReportsSuspenseQueryOptions(
    searchAdminInvoiceReportsRequest,
    params,
    options
  );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Deletes an invoice report from the administrator's perspective. Only reports in draft, canceled, or rejected status can be deleted.
Reports in waiting or approved status cannot be deleted. <br/>
**Restricted Access**: Only accessible to administrators and observers. <br/>

 * @summary Delete an invoice report from the administrator's perspective
 */
export const useDeleteAdminInvoiceReportHook = () => {
  const deleteAdminInvoiceReport = useCustomClient<void>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return deleteAdminInvoiceReport(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/destroy`,
        method: 'DELETE',
      },
      options
    );
  };
};

export const useDeleteAdminInvoiceReportMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportHook>>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const deleteAdminInvoiceReport = useDeleteAdminInvoiceReportHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportHook>>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteAdminInvoiceReport(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteAdminInvoiceReportMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportHook>>>
>;

export type DeleteAdminInvoiceReportMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Delete an invoice report from the administrator's perspective
 */
export const useDeleteAdminInvoiceReport = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportHook>>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}) => {
  const mutationOptions = useDeleteAdminInvoiceReportMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Destroys multiple invoice reports (payment requests) in a single request from the administrator's perspective.

Behaves identically to `/api/js/invoice_reports/bulk_destroy`, but is gated by administrator permissions instead of the requester's own scope.

**Restricted Access**: Only accessible to administrators and observers. <br/>

**Restrictions**:
- Maximum 50 reports per request
- Only destroyable reports can be destroyed (unsubmitted, canceled, or rejected status)
- Reports with status "waiting for approval" or "approved" cannot be destroyed

**Behavior**:
- If all reports are destroyed successfully, returns 200 OK with a success message
- If report_ids is missing, empty, or not an array, returns 400 Bad Request
- If any report cannot be destroyed (e.g. waiting/approved), returns 422 Unprocessable Entity
- If more than 50 reports are requested, returns 400 Bad Request

 * @summary Bulk destroy multiple invoice reports (admin)
 */
export const useBulkDestroyAdminInvoiceReportsHook = () => {
  const bulkDestroyAdminInvoiceReports =
    useCustomClient<BulkDestroyAdminInvoiceReports200>();

  return (
    bulkDestroyInvoiceReportsRequest: BodyType<BulkDestroyInvoiceReportsRequest>,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return bulkDestroyAdminInvoiceReports(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/bulk_destroy`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: bulkDestroyInvoiceReportsRequest,
      },
      options
    );
  };
};

export const useBulkDestroyAdminInvoiceReportsMutationOptions = <
  TError = ErrorType<
    CommonError | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useBulkDestroyAdminInvoiceReportsHook>>
    >,
    TError,
    { data: BodyType<BulkDestroyInvoiceReportsRequest> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useBulkDestroyAdminInvoiceReportsHook>>>,
  TError,
  { data: BodyType<BulkDestroyInvoiceReportsRequest> },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const bulkDestroyAdminInvoiceReports =
    useBulkDestroyAdminInvoiceReportsHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useBulkDestroyAdminInvoiceReportsHook>>
    >,
    { data: BodyType<BulkDestroyInvoiceReportsRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return bulkDestroyAdminInvoiceReports(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type BulkDestroyAdminInvoiceReportsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useBulkDestroyAdminInvoiceReportsHook>>>
>;
export type BulkDestroyAdminInvoiceReportsMutationBody =
  BodyType<BulkDestroyInvoiceReportsRequest>;
export type BulkDestroyAdminInvoiceReportsMutationError = ErrorType<
  CommonError | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

/**
 * @summary Bulk destroy multiple invoice reports (admin)
 */
export const useBulkDestroyAdminInvoiceReports = <
  TError = ErrorType<
    CommonError | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useBulkDestroyAdminInvoiceReportsHook>>
    >,
    TError,
    { data: BodyType<BulkDestroyInvoiceReportsRequest> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}) => {
  const mutationOptions =
    useBulkDestroyAdminInvoiceReportsMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Returns the summary information for an invoice report from the administrator's perspective. <br/>
**Restricted Access**: Only accessible to administrators and observers. <br/>

 * @summary Get admin invoice report summary
 */
export const useGetAdminInvoiceReportSummaryHook = () => {
  const getAdminInvoiceReportSummary =
    useCustomClient<GetAdminInvoiceReportSummaryResponse>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>,
    signal?: AbortSignal
  ) => {
    return getAdminInvoiceReportSummary(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/report_summary`,
        method: 'GET',
        signal,
      },
      options
    );
  };
};

export const getGetAdminInvoiceReportSummaryQueryKey = (id: string) => {
  return [
    `/api/js/invoice_reports/admin_invoice_reports/${id}/report_summary`,
  ] as const;
};

export const useGetAdminInvoiceReportSummaryQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportSummaryQueryKey(id);

  const getAdminInvoiceReportSummary = useGetAdminInvoiceReportSummaryHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>>
  > = ({ signal }) => getAdminInvoiceReportSummary(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportSummaryQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>>
>;
export type GetAdminInvoiceReportSummaryQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

/**
 * @summary Get admin invoice report summary
 */
export const useGetAdminInvoiceReportSummary = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportSummaryQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useGetAdminInvoiceReportSummarySuspenseQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportSummaryQueryKey(id);

  const getAdminInvoiceReportSummary = useGetAdminInvoiceReportSummaryHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>>
  > = ({ signal }) => getAdminInvoiceReportSummary(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportSummarySuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>>
>;
export type GetAdminInvoiceReportSummarySuspenseQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

/**
 * @summary Get admin invoice report summary
 */
export const useGetAdminInvoiceReportSummarySuspense = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportSummaryHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportSummarySuspenseQueryOptions(
    id,
    options
  );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Returns payment information for an invoice report from the administrator's perspective. <br/>
**Restricted Access**: Only accessible to administrators and observers.

 * @summary Get payment info for admin invoice report
 */
export const useGetAdminInvoiceReportPaymentInfoHook = () => {
  const getAdminInvoiceReportPaymentInfo =
    useCustomClient<AdminInvoiceReportPaymentInfoResponse>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>,
    signal?: AbortSignal
  ) => {
    return getAdminInvoiceReportPaymentInfo(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/payment_info`,
        method: 'GET',
        signal,
      },
      options
    );
  };
};

export const getGetAdminInvoiceReportPaymentInfoQueryKey = (id: string) => {
  return [
    `/api/js/invoice_reports/admin_invoice_reports/${id}/payment_info`,
  ] as const;
};

export const useGetAdminInvoiceReportPaymentInfoQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportPaymentInfoQueryKey(id);

  const getAdminInvoiceReportPaymentInfo =
    useGetAdminInvoiceReportPaymentInfoHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
    >
  > = ({ signal }) =>
    getAdminInvoiceReportPaymentInfo(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
    >,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportPaymentInfoQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
  >
>;
export type GetAdminInvoiceReportPaymentInfoQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

/**
 * @summary Get payment info for admin invoice report
 */
export const useGetAdminInvoiceReportPaymentInfo = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportPaymentInfoQueryOptions(
    id,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useGetAdminInvoiceReportPaymentInfoSuspenseQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportPaymentInfoQueryKey(id);

  const getAdminInvoiceReportPaymentInfo =
    useGetAdminInvoiceReportPaymentInfoHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
    >
  > = ({ signal }) =>
    getAdminInvoiceReportPaymentInfo(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseSuspenseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
    >,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportPaymentInfoSuspenseQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
  >
>;
export type GetAdminInvoiceReportPaymentInfoSuspenseQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

/**
 * @summary Get payment info for admin invoice report
 */
export const useGetAdminInvoiceReportPaymentInfoSuspense = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportPaymentInfoHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportPaymentInfoSuspenseQueryOptions(
    id,
    options
  );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Get journal of an admin invoice report
 */
export const useGetAdminInvoiceReportJournalHook = () => {
  const getAdminInvoiceReportJournal =
    useCustomClient<InvoiceReportJournalResponse>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>,
    signal?: AbortSignal
  ) => {
    return getAdminInvoiceReportJournal(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/journal`,
        method: 'GET',
        signal,
      },
      options
    );
  };
};

export const getGetAdminInvoiceReportJournalQueryKey = (id: string) => {
  return [
    `/api/js/invoice_reports/admin_invoice_reports/${id}/journal`,
  ] as const;
};

export const useGetAdminInvoiceReportJournalQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportJournalQueryKey(id);

  const getAdminInvoiceReportJournal = useGetAdminInvoiceReportJournalHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>>
  > = ({ signal }) => getAdminInvoiceReportJournal(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportJournalQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>>
>;
export type GetAdminInvoiceReportJournalQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Get journal of an admin invoice report
 */
export const useGetAdminInvoiceReportJournal = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportJournalQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useGetAdminInvoiceReportJournalSuspenseQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportJournalQueryKey(id);

  const getAdminInvoiceReportJournal = useGetAdminInvoiceReportJournalHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>>
  > = ({ signal }) => getAdminInvoiceReportJournal(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportJournalSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>>
>;
export type GetAdminInvoiceReportJournalSuspenseQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Get journal of an admin invoice report
 */
export const useGetAdminInvoiceReportJournalSuspense = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportJournalHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportJournalSuspenseQueryOptions(
    id,
    options
  );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Get invoice transactions of an admin invoice report
 */
export const useGetAdminInvoiceReportInvoiceTransactionsHook = () => {
  const getAdminInvoiceReportInvoiceTransactions =
    useCustomClient<InvoiceTransactionsResponse>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>,
    signal?: AbortSignal
  ) => {
    return getAdminInvoiceReportInvoiceTransactions(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/invoice_transactions`,
        method: 'GET',
        signal,
      },
      options
    );
  };
};

export const getGetAdminInvoiceReportInvoiceTransactionsQueryKey = (
  id: string
) => {
  return [
    `/api/js/invoice_reports/admin_invoice_reports/${id}/invoice_transactions`,
  ] as const;
};

export const useGetAdminInvoiceReportInvoiceTransactionsQueryOptions = <
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
    >
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetAdminInvoiceReportInvoiceTransactionsQueryKey(id);

  const getAdminInvoiceReportInvoiceTransactions =
    useGetAdminInvoiceReportInvoiceTransactionsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
      >
    >
  > = ({ signal }) =>
    getAdminInvoiceReportInvoiceTransactions(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportInvoiceTransactionsQueryResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
    >
  >
>;
export type GetAdminInvoiceReportInvoiceTransactionsQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Get invoice transactions of an admin invoice report
 */
export const useGetAdminInvoiceReportInvoiceTransactions = <
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
    >
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportInvoiceTransactionsQueryOptions(
    id,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useGetAdminInvoiceReportInvoiceTransactionsSuspenseQueryOptions = <
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
    >
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetAdminInvoiceReportInvoiceTransactionsQueryKey(id);

  const getAdminInvoiceReportInvoiceTransactions =
    useGetAdminInvoiceReportInvoiceTransactionsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
      >
    >
  > = ({ signal }) =>
    getAdminInvoiceReportInvoiceTransactions(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseSuspenseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportInvoiceTransactionsSuspenseQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
      >
    >
  >;
export type GetAdminInvoiceReportInvoiceTransactionsSuspenseQueryError =
  ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >;

/**
 * @summary Get invoice transactions of an admin invoice report
 */
export const useGetAdminInvoiceReportInvoiceTransactionsSuspense = <
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
    >
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportInvoiceTransactionsHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    useGetAdminInvoiceReportInvoiceTransactionsSuspenseQueryOptions(
      id,
      options
    );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Get a business document for an admin invoice report. <br/>
**Restricted Access**: Only accessible to administrators and observers. <br/>

 * @summary Get a business document for an admin invoice report
 */
export const useGetAdminInvoiceReportBusinessDocumentHook = () => {
  const getAdminInvoiceReportBusinessDocument =
    useCustomClient<AdminInvoiceReportBusinessDocumentResponse>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>,
    signal?: AbortSignal
  ) => {
    return getAdminInvoiceReportBusinessDocument(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/business_documents/${id}`,
        method: 'GET',
        signal,
      },
      options
    );
  };
};

export const getGetAdminInvoiceReportBusinessDocumentQueryKey = (
  id: string
) => {
  return [
    `/api/js/invoice_reports/admin_invoice_reports/business_documents/${id}`,
  ] as const;
};

export const useGetAdminInvoiceReportBusinessDocumentQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetAdminInvoiceReportBusinessDocumentQueryKey(id);

  const getAdminInvoiceReportBusinessDocument =
    useGetAdminInvoiceReportBusinessDocumentHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>
      >
    >
  > = ({ signal }) =>
    getAdminInvoiceReportBusinessDocument(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportBusinessDocumentQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>>
  >
>;
export type GetAdminInvoiceReportBusinessDocumentQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Get a business document for an admin invoice report
 */
export const useGetAdminInvoiceReportBusinessDocument = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportBusinessDocumentQueryOptions(
    id,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useGetAdminInvoiceReportBusinessDocumentSuspenseQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetAdminInvoiceReportBusinessDocumentQueryKey(id);

  const getAdminInvoiceReportBusinessDocument =
    useGetAdminInvoiceReportBusinessDocumentHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>
      >
    >
  > = ({ signal }) =>
    getAdminInvoiceReportBusinessDocument(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseSuspenseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportBusinessDocumentSuspenseQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>
      >
    >
  >;
export type GetAdminInvoiceReportBusinessDocumentSuspenseQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Get a business document for an admin invoice report
 */
export const useGetAdminInvoiceReportBusinessDocumentSuspense = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportBusinessDocumentHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    useGetAdminInvoiceReportBusinessDocumentSuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Returns approval route for an admin invoice report. <br/>
**Restricted Access**: Only accessible to administrators and observers.

 * @summary Get approval route for admin invoice report
 */
export const useGetAdminInvoiceReportApprovalRouteHook = () => {
  const getAdminInvoiceReportApprovalRoute =
    useCustomClient<AdminInvoiceReportApprovalRouteResponse>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>,
    signal?: AbortSignal
  ) => {
    return getAdminInvoiceReportApprovalRoute(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/approval_route`,
        method: 'GET',
        signal,
      },
      options
    );
  };
};

export const getGetAdminInvoiceReportApprovalRouteQueryKey = (id: string) => {
  return [
    `/api/js/invoice_reports/admin_invoice_reports/${id}/approval_route`,
  ] as const;
};

export const useGetAdminInvoiceReportApprovalRouteQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportApprovalRouteQueryKey(id);

  const getAdminInvoiceReportApprovalRoute =
    useGetAdminInvoiceReportApprovalRouteHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
    >
  > = ({ signal }) =>
    getAdminInvoiceReportApprovalRoute(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
    >,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportApprovalRouteQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
  >
>;
export type GetAdminInvoiceReportApprovalRouteQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Get approval route for admin invoice report
 */
export const useGetAdminInvoiceReportApprovalRoute = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportApprovalRouteQueryOptions(
    id,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useGetAdminInvoiceReportApprovalRouteSuspenseQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportApprovalRouteQueryKey(id);

  const getAdminInvoiceReportApprovalRoute =
    useGetAdminInvoiceReportApprovalRouteHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
    >
  > = ({ signal }) =>
    getAdminInvoiceReportApprovalRoute(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseSuspenseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
    >,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportApprovalRouteSuspenseQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
  >
>;
export type GetAdminInvoiceReportApprovalRouteSuspenseQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Get approval route for admin invoice report
 */
export const useGetAdminInvoiceReportApprovalRouteSuspense = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAdminInvoiceReportApprovalRouteHook>
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    useGetAdminInvoiceReportApprovalRouteSuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Returns the viewers for an invoice report from the administrator's perspective. <br/>
**Restricted Access**: Only accessible to administrators and observers.

 * @summary Get viewers for an admin invoice report
 */
export const useGetAdminInvoiceReportViewersHook = () => {
  const getAdminInvoiceReportViewers =
    useCustomClient<GetInvoiceReportViewersResponse>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>,
    signal?: AbortSignal
  ) => {
    return getAdminInvoiceReportViewers(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/viewers`,
        method: 'GET',
        signal,
      },
      options
    );
  };
};

export const getGetAdminInvoiceReportViewersQueryKey = (id: string) => {
  return [
    `/api/js/invoice_reports/admin_invoice_reports/${id}/viewers`,
  ] as const;
};

export const useGetAdminInvoiceReportViewersQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportViewersQueryKey(id);

  const getAdminInvoiceReportViewers = useGetAdminInvoiceReportViewersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>>
  > = ({ signal }) => getAdminInvoiceReportViewers(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportViewersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>>
>;
export type GetAdminInvoiceReportViewersQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Get viewers for an admin invoice report
 */
export const useGetAdminInvoiceReportViewers = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportViewersQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useGetAdminInvoiceReportViewersSuspenseQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminInvoiceReportViewersQueryKey(id);

  const getAdminInvoiceReportViewers = useGetAdminInvoiceReportViewersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>>
  > = ({ signal }) => getAdminInvoiceReportViewers(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminInvoiceReportViewersSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>>
>;
export type GetAdminInvoiceReportViewersSuspenseQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Get viewers for an admin invoice report
 */
export const useGetAdminInvoiceReportViewersSuspense = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminInvoiceReportViewersHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGetAdminInvoiceReportViewersSuspenseQueryOptions(
    id,
    options
  );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Attaches the given office members as viewers of the invoice_report, from the administrator's perspective. <br/>
Existing viewers not included in `office_member_ids` are left untouched (this is an additive operation, not a full replace). <br/>
**Restricted Access**: Only accessible to administrators and observers.

 * @summary Add viewers to an admin invoice report
 */
export const usePutAdminInvoiceReportViewersHook = () => {
  const putAdminInvoiceReportViewers = useCustomClient<void>();

  return (
    id: string,
    putInvoiceReportViewersRequest: BodyType<PutInvoiceReportViewersRequest>,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return putAdminInvoiceReportViewers(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/viewers`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: putInvoiceReportViewersRequest,
      },
      options
    );
  };
};

export const usePutAdminInvoiceReportViewersMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | CommonError
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePutAdminInvoiceReportViewersHook>>>,
    TError,
    { id: string; data: BodyType<PutInvoiceReportViewersRequest> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePutAdminInvoiceReportViewersHook>>>,
  TError,
  { id: string; data: BodyType<PutInvoiceReportViewersRequest> },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const putAdminInvoiceReportViewers = usePutAdminInvoiceReportViewersHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePutAdminInvoiceReportViewersHook>>>,
    { id: string; data: BodyType<PutInvoiceReportViewersRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return putAdminInvoiceReportViewers(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutAdminInvoiceReportViewersMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePutAdminInvoiceReportViewersHook>>>
>;
export type PutAdminInvoiceReportViewersMutationBody =
  BodyType<PutInvoiceReportViewersRequest>;
export type PutAdminInvoiceReportViewersMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | CommonError
>;

/**
 * @summary Add viewers to an admin invoice report
 */
export const usePutAdminInvoiceReportViewers = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | CommonError
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePutAdminInvoiceReportViewersHook>>>,
    TError,
    { id: string; data: BodyType<PutInvoiceReportViewersRequest> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}) => {
  const mutationOptions =
    usePutAdminInvoiceReportViewersMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Removes the given office members as viewers of the invoice_report, from the administrator's perspective. <br/>
**Restricted Access**: Only accessible to administrators and observers.

 * @summary Remove viewers from an admin invoice report
 */
export const useDeleteAdminInvoiceReportViewersHook = () => {
  const deleteAdminInvoiceReportViewers = useCustomClient<void>();

  return (
    id: string,
    deleteInvoiceReportViewersRequest: BodyType<DeleteInvoiceReportViewersRequest>,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return deleteAdminInvoiceReportViewers(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/viewers`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        data: deleteInvoiceReportViewersRequest,
      },
      options
    );
  };
};

export const useDeleteAdminInvoiceReportViewersMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | CommonError
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportViewersHook>>
    >,
    TError,
    { id: string; data: BodyType<DeleteInvoiceReportViewersRequest> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportViewersHook>>
  >,
  TError,
  { id: string; data: BodyType<DeleteInvoiceReportViewersRequest> },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const deleteAdminInvoiceReportViewers =
    useDeleteAdminInvoiceReportViewersHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportViewersHook>>
    >,
    { id: string; data: BodyType<DeleteInvoiceReportViewersRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return deleteAdminInvoiceReportViewers(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteAdminInvoiceReportViewersMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportViewersHook>>>
>;
export type DeleteAdminInvoiceReportViewersMutationBody =
  BodyType<DeleteInvoiceReportViewersRequest>;
export type DeleteAdminInvoiceReportViewersMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | CommonError
>;

/**
 * @summary Remove viewers from an admin invoice report
 */
export const useDeleteAdminInvoiceReportViewers = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | CommonError
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useDeleteAdminInvoiceReportViewersHook>>
    >,
    TError,
    { id: string; data: BodyType<DeleteInvoiceReportViewersRequest> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}) => {
  const mutationOptions =
    useDeleteAdminInvoiceReportViewersMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancels an invoice report from the administrator's perspective. <br/>
**Restricted Access**: Only accessible to administrators. Observers cannot cancel. <br/>
**Business Rules**:
- Cannot cancel if the report is unsubmitted, already canceled, or rejected
- Cannot cancel if the report belongs to a report unit
- Cannot cancel if the report is approved and the report type does not allow cancellation after approval

 * @summary Cancel an admin invoice report
 */
export const useCancelAdminInvoiceReportHook = () => {
  const cancelAdminInvoiceReport =
    useCustomClient<CancelInvoiceReportResponse>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return cancelAdminInvoiceReport(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/${id}/cancel`,
        method: 'POST',
      },
      options
    );
  };
};

export const useCancelAdminInvoiceReportMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | CommonError | NotFoundResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCancelAdminInvoiceReportHook>>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCancelAdminInvoiceReportHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const cancelAdminInvoiceReport = useCancelAdminInvoiceReportHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCancelAdminInvoiceReportHook>>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return cancelAdminInvoiceReport(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelAdminInvoiceReportMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCancelAdminInvoiceReportHook>>>
>;

export type CancelAdminInvoiceReportMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | CommonError | NotFoundResponse
>;

/**
 * @summary Cancel an admin invoice report
 */
export const useCancelAdminInvoiceReport = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | CommonError | NotFoundResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCancelAdminInvoiceReportHook>>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}) => {
  const mutationOptions = useCancelAdminInvoiceReportMutationOptions(options);

  return useMutation(mutationOptions);
};
